import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  TrendingUp,
  UserPlus,
  CalendarCheck,
  Trophy,
  XCircle,
  Mail,
  Phone,
  MessageSquare,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { can, useAuth } from "@/contexts/AuthContext";

export const LEAD_STAGES = [
  { key: "new_lead", label: "New Lead", short: "New", tone: "bg-blue-50 border-blue-200 text-blue-900", dot: "bg-blue-500" },
  { key: "contacted", label: "Contacted", short: "Contacted", tone: "bg-indigo-50 border-indigo-200 text-indigo-900", dot: "bg-indigo-500" },
  { key: "consultation_booked", label: "Consultation Booked", short: "Consult", tone: "bg-violet-50 border-violet-200 text-violet-900", dot: "bg-violet-500" },
  { key: "proposal_sent", label: "Proposal Sent", short: "Proposal", tone: "bg-amber-50 border-amber-200 text-amber-900", dot: "bg-amber-500" },
  { key: "won", label: "Won / Active Client", short: "Won", tone: "bg-emerald-50 border-emerald-200 text-emerald-900", dot: "bg-emerald-500" },
  { key: "lost", label: "Lost / Not a Fit", short: "Lost", tone: "bg-slate-100 border-slate-300 text-slate-700", dot: "bg-slate-400" },
];

function stageMeta(key) {
  return LEAD_STAGES.find((s) => s.key === key) || LEAD_STAGES[0];
}

function ContactIcon({ method }) {
  if (method === "email") return <Mail size={12} className="text-slate-500" />;
  if (method === "text") return <MessageSquare size={12} className="text-slate-500" />;
  return <Phone size={12} className="text-slate-500" />;
}

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function LeadPipeline({ clients, onClientsChange, onOpenClient }) {
  const { user: currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const canEdit = can(currentUser, "scheduleSession"); // admin, trainer, front_desk

  const refreshStats = useCallback(async () => {
    try {
      const res = await api.get("/leads/stats");
      setStats(res.data);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    refreshStats();
  }, [clients, refreshStats]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(LEAD_STAGES.map((s) => [s.key, []]));
    for (const c of clients) {
      const k = c.leadStage || "new_lead";
      if (g[k]) g[k].push(c);
    }
    // Newest first within each column
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
    }
    return g;
  }, [clients]);

  async function changeStage(clientId, newStage) {
    if (!canEdit) {
      toast.error("You don't have permission to change pipeline stages");
      return;
    }
    const client = clients.find((c) => c.id === clientId);
    if (!client || client.leadStage === newStage) return;
    // Optimistic update
    const original = client.leadStage;
    onClientsChange((prev) => prev.map((c) => (c.id === clientId ? { ...c, leadStage: newStage } : c)));
    try {
      const res = await api.put(`/clients/${clientId}`, { leadStage: newStage });
      onClientsChange((prev) => prev.map((c) => (c.id === clientId ? res.data : c)));
      const label = stageMeta(newStage).label;
      toast.success(`${client.dogName || client.ownerName} → ${label}`);
    } catch (err) {
      onClientsChange((prev) => prev.map((c) => (c.id === clientId ? { ...c, leadStage: original } : c)));
      toast.error("Failed to change stage");
    }
  }

  const headline = stats?.headline || { newLeads: 0, bookedConsults: 0, wonClients: 0, lostLeads: 0 };

  return (
    <div className="space-y-6" data-testid="pipeline-view">
      {/* Headline metrics */}
      <div className="grid gap-3 md:grid-cols-4">
        <MetricCard icon={<UserPlus size={18} />} label="New Leads" value={headline.newLeads} tone="text-blue-700" />
        <MetricCard icon={<CalendarCheck size={18} />} label="Booked Consults" value={headline.bookedConsults} tone="text-violet-700" />
        <MetricCard icon={<Trophy size={18} />} label="Won Clients" value={headline.wonClients} tone="text-emerald-700" />
        <MetricCard icon={<XCircle size={18} />} label="Lost Leads" value={headline.lostLeads} tone="text-slate-500" />
      </div>

      {/* Kanban */}
      <div
        className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 md:snap-none"
        data-testid="kanban-board"
      >
        {LEAD_STAGES.map((stage) => {
          const cards = grouped[stage.key] || [];
          const isDropTarget = overStage === stage.key && draggingId;
          return (
            <div
              key={stage.key}
              data-testid={`kanban-column-${stage.key}`}
              className={`min-w-[280px] snap-start flex-shrink-0 rounded-2xl border bg-white/70 p-3 transition md:min-w-0 md:flex-1 ${
                isDropTarget ? "border-slate-900 ring-2 ring-slate-300" : "border-slate-200"
              }`}
              onDragOver={(e) => {
                if (!canEdit) return;
                e.preventDefault();
                setOverStage(stage.key);
              }}
              onDragLeave={() => setOverStage(null)}
              onDrop={(e) => {
                e.preventDefault();
                setOverStage(null);
                if (draggingId) {
                  changeStage(draggingId, stage.key);
                  setDraggingId(null);
                }
              }}
            >
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${stage.dot}`} />
                  <h4 className="text-sm font-semibold">{stage.label}</h4>
                </div>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                  {cards.length}
                </span>
              </div>
              <div className="space-y-2">
                {cards.length === 0 && (
                  <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">No clients in this stage.</p>
                )}
                {cards.map((c) => (
                  <LeadCard
                    key={c.id}
                    client={c}
                    canEdit={canEdit}
                    isDragging={draggingId === c.id}
                    onDragStart={() => setDraggingId(c.id)}
                    onDragEnd={() => setDraggingId(null)}
                    onStageChange={(s) => changeStage(c.id, s)}
                    onOpen={() => onOpenClient?.(c.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Conversion by source */}
      <Card className="rounded-2xl shadow-sm">
        <CardContent className="p-5">
          <h3 className="mb-3 flex items-center gap-2 text-lg font-bold">
            <TrendingUp size={18} /> Conversion by Source
          </h3>
          {!stats?.bySource?.length && <p className="text-sm text-slate-500">No source data yet.</p>}
          {stats?.bySource?.length > 0 && (
            <div className="overflow-x-auto" data-testid="conversion-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2 pr-3 text-right">Total Leads</th>
                    <th className="py-2 pr-3 text-right">Won</th>
                    <th className="py-2 text-right">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.bySource.map((row) => (
                    <tr key={row.source} className="border-b last:border-0">
                      <td className="py-2 pr-3 font-medium">{row.source}</td>
                      <td className="py-2 pr-3 text-right text-slate-700">{row.total}</td>
                      <td className="py-2 pr-3 text-right text-emerald-700">{row.won}</td>
                      <td className="py-2 text-right">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            row.conversionRate >= 30
                              ? "bg-emerald-100 text-emerald-800"
                              : row.conversionRate >= 10
                                ? "bg-amber-100 text-amber-800"
                                : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {row.conversionRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ icon, label, value, tone }) {
  return (
    <Card className="rounded-2xl shadow-sm">
      <CardContent className="flex items-center justify-between p-5">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className={`mt-1 text-3xl font-bold ${tone}`}>{value}</p>
        </div>
        <span className={`rounded-2xl bg-slate-50 p-3 ${tone}`}>{icon}</span>
      </CardContent>
    </Card>
  );
}

function LeadCard({ client, canEdit, isDragging, onDragStart, onDragEnd, onStageChange, onOpen }) {
  const currIdx = LEAD_STAGES.findIndex((s) => s.key === (client.leadStage || "new_lead"));
  const prevStage = currIdx > 0 ? LEAD_STAGES[currIdx - 1] : null;
  const nextStage = currIdx >= 0 && currIdx < LEAD_STAGES.length - 1 ? LEAD_STAGES[currIdx + 1] : null;

  return (
    <motion.div
      layout
      draggable={canEdit}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid={`lead-card-${client.id}`}
      className={`group cursor-pointer rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:shadow ${
        isDragging ? "opacity-50" : ""
      }`}
      onClick={onOpen}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-slate-900">{client.dogName || "(unnamed dog)"}</p>
          <p className="truncate text-xs text-slate-600">{client.ownerName || "(unknown)"}</p>
        </div>
        <p className="flex-shrink-0 text-xs text-slate-400">{shortDate(client.createdAt)}</p>
      </div>

      {client.mainBehaviorIssue && (
        <p className="mt-2 line-clamp-2 text-xs text-slate-700">{client.mainBehaviorIssue}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        {client.preferredContact && (
          <span className="inline-flex items-center gap-1">
            <ContactIcon method={client.preferredContact} />
            <span className="capitalize">{client.preferredContact}</span>
          </span>
        )}
        {client.source && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">
            {client.source}
          </span>
        )}
      </div>

      {canEdit && (
        <div
          className="mt-3 flex items-center justify-between gap-1 border-t border-slate-100 pt-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="outline"
            disabled={!prevStage}
            onClick={() => prevStage && onStageChange(prevStage.key)}
            className="h-7 rounded-lg px-2 text-xs"
            data-testid={`lead-prev-${client.id}`}
            title={prevStage ? `Move to ${prevStage.label}` : ""}
          >
            <ArrowLeft size={12} />
          </Button>
          <select
            value={client.leadStage || "new_lead"}
            onChange={(e) => onStageChange(e.target.value)}
            className="flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs"
            data-testid={`lead-stage-select-${client.id}`}
          >
            {LEAD_STAGES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            disabled={!nextStage}
            onClick={() => nextStage && onStageChange(nextStage.key)}
            className="h-7 rounded-lg px-2 text-xs"
            data-testid={`lead-next-${client.id}`}
            title={nextStage ? `Move to ${nextStage.label}` : ""}
          >
            <ArrowRight size={12} />
          </Button>
        </div>
      )}
    </motion.div>
  );
}
